
import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiV2 } from '../services/api';
import LocalStorageService from '../services/storage';

const localStorageService = LocalStorageService();

interface PaymentType {
    id_paymenttype: string;
    paymenttype_name: string;
    isactive?: boolean;
}
type PaymentTypeInput = Omit<PaymentType, 'id_paymenttype'>

interface PaymentTypesProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface PaymentTypesContextData {
    createPaymentType: (paymentType: PaymentTypeInput) => Promise<ResponseProps>;
    updatePaymentType: (paymentType: PaymentType) => Promise<ResponseProps>;
    updateOnDelete: (id: string | number) => Promise<ResponseProps>;
}

interface UsePaymentTypesOptions {
    loadPaymentTypes?: boolean;
}

interface UsePaymentTypesData extends PaymentTypesContextData {
    paymentTypes: PaymentType[];
}

const PaymentTypesContext = createContext<PaymentTypesContextData>(
    {} as PaymentTypesContextData
);

const paymentTypesQueryKey = ['payment-types'];
const emptyPaymentTypes: PaymentType[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

async function fetchPaymentTypes() {
  const response = await apiV2.get('payment-types');
  return Array.isArray(response?.data) ? response.data : [];
}

export function PaymentTypesProvider({ children }: PaymentTypesProvidersProps) {
  const queryClient = useQueryClient();

  function invalidatePaymentTypes() {
    queryClient.invalidateQueries({ queryKey: paymentTypesQueryKey });
  }

  async function updateOnDelete(id: string | number) {
    const response = await apiV2.delete(`/payment-types/${id}`);
    if (response.status === 200) {
      invalidatePaymentTypes();
      return {
        success: true,
        message: "Tipo de pagamento removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover tipo de pagamento."
    };
  }

  async function createPaymentType(paymentTypesInput: PaymentTypeInput) {
    const response = await apiV2.post('/payment-types', paymentTypesInput);
    if (response.status === 201) {
      invalidatePaymentTypes();
      return {
        success: true,
        message: "Tipo de pagamento adicionado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar tipo de pagamento."
    };

  }

  async function updatePaymentType(paymentType: PaymentType) {
    const response = await apiV2.put(`/payment-types/${paymentType.id_paymenttype}`, paymentType);
    if (response.status === 204) {
      invalidatePaymentTypes();
      return {
        success: true,
        message: "Tipo de pagamento editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar tipo de pagamento."
    };
  }

  return (
    <PaymentTypesContext.Provider value={{ createPaymentType, updatePaymentType, updateOnDelete }}>
      {children}
    </PaymentTypesContext.Provider>
  );
}

export function UsePaymentTypes(options: UsePaymentTypesOptions = {}): UsePaymentTypesData {
  const context = useContext(PaymentTypesContext);
  const { loadPaymentTypes = false } = options;

  const paymentTypesQuery = useQuery({
    queryKey: paymentTypesQueryKey,
    queryFn: fetchPaymentTypes,
    enabled: loadPaymentTypes && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...context,
    paymentTypes: paymentTypesQuery.data || emptyPaymentTypes
  };
}
